import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

interface UseApiOptions {
  /** false bo'lsa fetch qilinmaydi (masalan tgId hali tayyor bo'lmasa) */
  enabled?: boolean;
}

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  setData: Dispatch<SetStateAction<T | null>>;
}

/**
 * Standart sahifa data-fetch patterni:
 *   loading → <ShimmerSkeleton>, error → <ErrorState onRetry={refetch}>, data → kontent
 */
export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
  opts: UseApiOptions = {},
): UseApiResult<T> {
  const { enabled = true } = opts;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  // Kechikkan javob eskirgan state ustiga yozmasligi uchun
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      if (requestId === requestIdRef.current) {
        setData(result);
        setLoading(false);
      }
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return { data, loading, error, refetch: load, setData };
}

type Fetchers = Record<string, () => Promise<unknown>>;

type SettledResults<F extends Fetchers> = {
  [K in keyof F]: Awaited<ReturnType<F[K]>> | null;
};

interface UseApiAllResult<F extends Fetchers> {
  data: SettledResults<F> | null;
  loading: boolean;
  /** Hammasi xato bo'lsa true — sahifa darajasida ErrorState uchun */
  error: boolean;
  /** Qisman xatolar — seksiya darajasida ErrorState uchun */
  errors: Partial<Record<keyof F, Error>>;
  refetch: () => Promise<void>;
}

/**
 * Bir sahifadagi bir nechta mustaqil fetch'ni bitta hookga yig'adi.
 * Promise.allSettled — bitta endpoint yiqilsa qolganlari baribir ko'rsatiladi.
 */
export function useApiAll<F extends Fetchers>(
  fetchers: F,
  deps: unknown[] = [],
  opts: UseApiOptions = {},
): UseApiAllResult<F> {
  const { enabled = true } = opts;
  const [data, setData] = useState<SettledResults<F> | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof F, Error>>>({});
  const [loading, setLoading] = useState(enabled);

  const fetchersRef = useRef(fetchers);
  fetchersRef.current = fetchers;
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    const keys = Object.keys(fetchersRef.current) as (keyof F)[];
    const settled = await Promise.allSettled(keys.map((key) => fetchersRef.current[key]()));
    if (requestId !== requestIdRef.current) return;

    const nextData = {} as SettledResults<F>;
    const nextErrors: Partial<Record<keyof F, Error>> = {};
    settled.forEach((result, i) => {
      const key = keys[i];
      if (result.status === "fulfilled") {
        nextData[key] = result.value as SettledResults<F>[keyof F];
      } else {
        nextData[key] = null;
        nextErrors[key] =
          result.reason instanceof Error ? result.reason : new Error(String(result.reason));
      }
    });
    setData(nextData);
    setErrors(nextErrors);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  const allFailed =
    data !== null &&
    Object.keys(data).length > 0 &&
    Object.values(data).every((value) => value === null);

  return { data, loading, error: allFailed, errors, refetch: load };
}
